/**
 * Keyboard Navigation Manager
 * Provides full keyboard navigation support for the game
 * Implements WCAG 2.1 AA keyboard accessibility requirements
 */

export interface FocusableElement {
  id: string;
  element: HTMLElement | null;
  gameObject?: unknown;
  onActivate: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  group?: string;
}

export interface FocusGroup {
  id: string;
  elements: string[];
  wrapAround: boolean;
  orientation: 'horizontal' | 'vertical' | 'grid';
  gridColumns?: number;
}

export class KeyboardNavigation {
  private enabled = false;
  private focusableElements: Map<string, FocusableElement> = new Map();
  private focusGroups: Map<string, FocusGroup> = new Map();
  private currentFocusId: string | null = null;
  private currentGroupId: string | null = null;
  private focusIndicator: HTMLDivElement | null = null;
  private styleElement: HTMLStyleElement | null = null;
  private keydownHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor() {
    this.createFocusIndicator();
    this.createStyles();
  }

  private createFocusIndicator(): void {
    if (typeof document === 'undefined') return;

    this.focusIndicator = document.createElement('div');
    this.focusIndicator.id = 'keyboard-focus-indicator';
    this.focusIndicator.setAttribute('aria-hidden', 'true');
    this.focusIndicator.style.cssText = `
      position: fixed;
      pointer-events: none;
      z-index: 99999;
      border: 3px solid #00d4ff;
      border-radius: 4px;
      box-shadow: 0 0 0 2px rgba(0, 212, 255, 0.3), 0 0 10px rgba(0, 212, 255, 0.5);
      transition: all 0.15s ease-out;
      opacity: 0;
    `;
    document.body.appendChild(this.focusIndicator);
  }

  private createStyles(): void {
    if (typeof document === 'undefined') return;

    this.styleElement = document.createElement('style');
    this.styleElement.id = 'keyboard-navigation-styles';
    this.styleElement.textContent = `
      /* Hide focus indicator when using mouse */
      body:not(.keyboard-navigation) #keyboard-focus-indicator {
        opacity: 0 !important;
      }

      /* Show focus indicator in keyboard mode */
      body.keyboard-navigation #keyboard-focus-indicator {
        opacity: 1;
      }

      /* Skip link for screen readers */
      .skip-link {
        position: fixed;
        top: -100px;
        left: 0;
        padding: 8px 16px;
        background: #00d4ff;
        color: #040d1a;
        font-weight: bold;
        z-index: 100000;
        transition: top 0.2s;
      }

      .skip-link:focus {
        top: 0;
      }

      /* Focus visible fallback for browsers without :focus-visible */
      body.keyboard-navigation *:focus {
        outline: 2px solid #00d4ff;
        outline-offset: 2px;
      }

      /* Hide outline for mouse users */
      body:not(.keyboard-navigation) *:focus {
        outline: none;
      }

      /* Custom focus styles for game elements */
      .game-focusable:focus {
        outline: none;
      }

      body.keyboard-navigation .game-focusable:focus {
        box-shadow: 0 0 0 3px #00d4ff, 0 0 10px rgba(0, 212, 255, 0.5);
      }
    `;
    document.head.appendChild(this.styleElement);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;

    if (typeof document === 'undefined') return;

    if (enabled) {
      this.attachListeners();
      document.body.classList.add('keyboard-navigation');
    } else {
      this.detachListeners();
      document.body.classList.remove('keyboard-navigation');
      this.hideFocusIndicator();
    }
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  private attachListeners(): void {
    if (typeof document === 'undefined') return;

    this.keydownHandler = this.handleKeydown.bind(this);
    document.addEventListener('keydown', this.keydownHandler);

    // Detect keyboard vs mouse usage
    document.addEventListener('mousedown', this.handleMouseDown.bind(this));
    document.addEventListener('keydown', this.handleFirstKeydown.bind(this), { once: true });
  }

  private detachListeners(): void {
    if (typeof document === 'undefined') return;

    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
    }
  }

  private handleMouseDown(): void {
    if (typeof document === 'undefined') return;
    document.body.classList.remove('keyboard-navigation');
    this.hideFocusIndicator();
  }

  private handleFirstKeydown(e: KeyboardEvent): void {
    if (e.key === 'Tab') {
      if (typeof document !== 'undefined') {
        document.body.classList.add('keyboard-navigation');
      }
    }
  }

  private handleKeydown(e: KeyboardEvent): void {
    if (!this.enabled) return;

    switch (e.key) {
      case 'Tab':
        e.preventDefault();
        if (e.shiftKey) {
          this.focusPrevious();
        } else {
          this.focusNext();
        }
        break;

      case 'Enter':
      case ' ':
        e.preventDefault();
        this.activateCurrent();
        break;

      case 'ArrowUp':
        e.preventDefault();
        this.navigateInGroup('up');
        break;

      case 'ArrowDown':
        e.preventDefault();
        this.navigateInGroup('down');
        break;

      case 'ArrowLeft':
        e.preventDefault();
        this.navigateInGroup('left');
        break;

      case 'ArrowRight':
        e.preventDefault();
        this.navigateInGroup('right');
        break;

      case 'Escape':
        this.handleEscape();
        break;

      case 'Home':
        e.preventDefault();
        this.focusFirst();
        break;

      case 'End':
        e.preventDefault();
        this.focusLast();
        break;
    }
  }

  // Register a focusable element
  register(element: FocusableElement): void {
    this.focusableElements.set(element.id, element);

    if (element.group) {
      this.addToGroup(element.id, element.group);
    }
  }

  // Unregister a focusable element
  unregister(id: string): void {
    const element = this.focusableElements.get(id);
    if (element?.group) {
      this.removeFromGroup(id, element.group);
    }
    this.focusableElements.delete(id);

    if (this.currentFocusId === id) {
      this.currentFocusId = null;
      this.hideFocusIndicator();
    }
  }

  // Create a focus group
  createGroup(group: FocusGroup): void {
    this.focusGroups.set(group.id, group);
  }

  // Remove a focus group
  removeGroup(id: string): void {
    this.focusGroups.delete(id);
    if (this.currentGroupId === id) {
      this.currentGroupId = null;
    }
  }

  private addToGroup(elementId: string, groupId: string): void {
    const group = this.focusGroups.get(groupId);
    if (group && !group.elements.includes(elementId)) {
      group.elements.push(elementId);
    }
  }

  private removeFromGroup(elementId: string, groupId: string): void {
    const group = this.focusGroups.get(groupId);
    if (group) {
      group.elements = group.elements.filter((id) => id !== elementId);
    }
  }

  // Focus management
  focus(id: string): void {
    const element = this.focusableElements.get(id);
    if (!element) return;

    // Blur previous
    if (this.currentFocusId && this.currentFocusId !== id) {
      const prev = this.focusableElements.get(this.currentFocusId);
      prev?.onBlur?.();
    }

    this.currentFocusId = id;
    this.currentGroupId = element.group || null;

    // Focus the element
    element.onFocus?.();

    // Update focus indicator position
    this.updateFocusIndicator(element);
  }

  private focusNext(): void {
    const elements = Array.from(this.focusableElements.keys());
    if (elements.length === 0) return;

    const currentIndex = this.currentFocusId ? elements.indexOf(this.currentFocusId) : -1;
    const nextIndex = (currentIndex + 1) % elements.length;
    this.focus(elements[nextIndex]);
  }

  private focusPrevious(): void {
    const elements = Array.from(this.focusableElements.keys());
    if (elements.length === 0) return;

    const currentIndex = this.currentFocusId ? elements.indexOf(this.currentFocusId) : 0;
    const prevIndex = (currentIndex - 1 + elements.length) % elements.length;
    this.focus(elements[prevIndex]);
  }

  private focusFirst(): void {
    const elements = Array.from(this.focusableElements.keys());
    if (elements.length > 0) {
      this.focus(elements[0]);
    }
  }

  private focusLast(): void {
    const elements = Array.from(this.focusableElements.keys());
    if (elements.length > 0) {
      this.focus(elements[elements.length - 1]);
    }
  }

  private navigateInGroup(direction: 'up' | 'down' | 'left' | 'right'): void {
    if (!this.currentGroupId || !this.currentFocusId) {
      // If not in a group, use simple navigation
      if (direction === 'down' || direction === 'right') {
        this.focusNext();
      } else {
        this.focusPrevious();
      }
      return;
    }

    const group = this.focusGroups.get(this.currentGroupId);
    if (!group) return;

    const currentIndex = group.elements.indexOf(this.currentFocusId);
    if (currentIndex === -1) return;

    let nextIndex: number;

    if (group.orientation === 'grid' && group.gridColumns) {
      // Grid navigation
      const cols = group.gridColumns;
      const row = Math.floor(currentIndex / cols);
      const col = currentIndex % cols;

      switch (direction) {
        case 'up':
          nextIndex = row > 0 ? currentIndex - cols : (group.wrapAround ? (Math.floor(group.elements.length / cols) * cols + col) : currentIndex);
          break;
        case 'down':
          nextIndex = currentIndex + cols < group.elements.length ? currentIndex + cols : (group.wrapAround ? col : currentIndex);
          break;
        case 'left':
          nextIndex = col > 0 ? currentIndex - 1 : (group.wrapAround ? currentIndex + cols - 1 : currentIndex);
          break;
        case 'right':
          nextIndex = col < cols - 1 && currentIndex + 1 < group.elements.length ? currentIndex + 1 : (group.wrapAround ? currentIndex - col : currentIndex);
          break;
      }
    } else if (group.orientation === 'vertical') {
      if (direction === 'up') {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : (group.wrapAround ? group.elements.length - 1 : currentIndex);
      } else if (direction === 'down') {
        nextIndex = currentIndex < group.elements.length - 1 ? currentIndex + 1 : (group.wrapAround ? 0 : currentIndex);
      } else {
        return;
      }
    } else {
      // Horizontal
      if (direction === 'left') {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : (group.wrapAround ? group.elements.length - 1 : currentIndex);
      } else if (direction === 'right') {
        nextIndex = currentIndex < group.elements.length - 1 ? currentIndex + 1 : (group.wrapAround ? 0 : currentIndex);
      } else {
        return;
      }
    }

    if (nextIndex !== undefined && nextIndex !== currentIndex && group.elements[nextIndex]) {
      this.focus(group.elements[nextIndex]);
    }
  }

  private activateCurrent(): void {
    if (!this.currentFocusId) return;

    const element = this.focusableElements.get(this.currentFocusId);
    element?.onActivate();
  }

  private handleEscape(): void {
    // Can be extended to close modals, menus, etc.
    this.hideFocusIndicator();
    this.currentFocusId = null;
  }

  private updateFocusIndicator(element: FocusableElement): void {
    if (!this.focusIndicator || !element.element) return;

    const rect = element.element.getBoundingClientRect();
    const padding = 4;

    this.focusIndicator.style.left = `${rect.left - padding}px`;
    this.focusIndicator.style.top = `${rect.top - padding}px`;
    this.focusIndicator.style.width = `${rect.width + padding * 2}px`;
    this.focusIndicator.style.height = `${rect.height + padding * 2}px`;
    this.focusIndicator.style.opacity = '1';
  }

  private hideFocusIndicator(): void {
    if (this.focusIndicator) {
      this.focusIndicator.style.opacity = '0';
    }
  }

  // Get current focus state
  getCurrentFocus(): string | null {
    return this.currentFocusId;
  }

  getCurrentGroup(): string | null {
    return this.currentGroupId;
  }

  // Clear all registrations (useful when changing scenes)
  clearAll(): void {
    this.focusableElements.clear();
    this.focusGroups.clear();
    this.currentFocusId = null;
    this.currentGroupId = null;
    this.hideFocusIndicator();
  }

  // Clean up
  destroy(): void {
    this.detachListeners();
    this.clearAll();

    if (this.focusIndicator && this.focusIndicator.parentNode) {
      this.focusIndicator.parentNode.removeChild(this.focusIndicator);
    }
    if (this.styleElement && this.styleElement.parentNode) {
      this.styleElement.parentNode.removeChild(this.styleElement);
    }
  }
}
